import { supabase } from './supabase';

export interface BusinessMetric {
  id: string;
  business_id: string;
  metric_type: string;
  metric_value: number;
  target_value?: number;
  timestamp: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface AIInsight {
  id: string;
  business_id: string;
  insight_type: string;
  insight_content: string;
  confidence_score?: number;
  source_data?: Record<string, any>;
  is_actionable: boolean;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface DataSource {
  id: string;
  business_id: string;
  source_type: string;
  source_name: string;
  connection_details?: Record<string, any>;
  status: string;
  last_sync_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AnalyticsGoal {
  id: string;
  business_id: string;
  goal_type: string;
  target_value: number;
  current_value: number;
  start_date: string;
  end_date: string;
  status: 'active' | 'completed' | 'expired';
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface AnalyticsAlert {
  id: string;
  business_id: string;
  alert_type: string;
  alert_message: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  is_read: boolean;
  metadata?: Record<string, any>;
  created_at: string;
}

const withRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> => {
  let lastError: Error;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Operation failed');
      if (i === maxRetries - 1) break;
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, i)));
    }
  }
  throw lastError!;
};

export const analytics = {
  // Business Metrics
  async getBusinessMetrics(businessId: string, metricType?: string): Promise<BusinessMetric[]> {
    return withRetry(async () => {
      const query = supabase
        .from('business_metrics')
        .select('*')
        .eq('business_id', businessId)
        .order('timestamp', { ascending: false });

      if (metricType) {
        query.eq('metric_type', metricType);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    });
  },

  async addBusinessMetric(metric: Omit<BusinessMetric, 'id' | 'created_at' | 'updated_at'>) {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('business_metrics')
        .insert(metric)
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  },

  // AI Insights
  async getAIInsights(businessId: string, insightType?: string): Promise<AIInsight[]> {
    return withRetry(async () => {
      const query = supabase
        .from('ai_insights')
        .select('*')
        .eq('business_id', businessId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (insightType) {
        query.eq('insight_type', insightType);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    });
  },

  async addAIInsight(insight: Omit<AIInsight, 'id' | 'created_at' | 'updated_at'>) {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('ai_insights')
        .insert(insight)
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  },

  // Analytics Goals
  async getAnalyticsGoals(businessId: string, status?: string): Promise<AnalyticsGoal[]> {
    return withRetry(async () => {
      const query = supabase
        .from('analytics_goals')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (status) {
        query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    });
  },

  async createAnalyticsGoal(goal: Omit<AnalyticsGoal, 'id' | 'created_at' | 'updated_at' | 'status'>) {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('analytics_goals')
        .insert(goal)
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  },

  async updateAnalyticsGoal(goalId: string, updates: Partial<AnalyticsGoal>) {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('analytics_goals')
        .update(updates)
        .eq('id', goalId)
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  },

  // Analytics Alerts
  async getAnalyticsAlerts(businessId: string, unreadOnly = false): Promise<AnalyticsAlert[]> {
    return withRetry(async () => {
      const query = supabase
        .from('analytics_alerts')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (unreadOnly) {
        query.eq('is_read', false);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    });
  },

  async markAlertAsRead(alertId: string) {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('analytics_alerts')
        .update({ is_read: true })
        .eq('id', alertId)
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  },

  async markAllAlertsAsRead(businessId: string) {
    return withRetry(async () => {
      const { error } = await supabase
        .from('analytics_alerts')
        .update({ is_read: true })
        .eq('business_id', businessId)
        .eq('is_read', false);

      if (error) throw error;
    });
  },

  // Data Sources
  async getDataSources(businessId: string): Promise<DataSource[]> {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('data_sources')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    });
  },

  async addDataSource(source: Omit<DataSource, 'id' | 'created_at' | 'updated_at'>) {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('data_sources')
        .insert(source)
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  },

  async updateDataSourceStatus(sourceId: string, status: string) {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('data_sources')
        .update({ status })
        .eq('id', sourceId)
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  },

  // User Actions
  async trackUserAction(
    userId: string,
    actionType: string,
    actionDetails?: Record<string, any>,
    pageUrl?: string
  ) {
    return withRetry(async () => {
      const clientInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenSize: {
          width: window.screen.width,
          height: window.screen.height
        },
        timestamp: new Date().toISOString()
      };

      const { error } = await supabase
        .from('user_actions')
        .insert({
          user_id: userId,
          action_type: actionType,
          action_details: actionDetails,
          page_url: pageUrl,
          client_info: clientInfo
        });

      if (error) throw error;
    });
  }
};